import { Typography } from "@mui/material";
import { css } from "@emotion/react";
import { useState } from "react";
import { Button } from "../../Button";
import { CSVImportUIColumns } from "../CSVImportUI";
import { ScreenLayout } from "../ScreenLayout";
import { TableUI } from "../../TableUI";
import { createEmptyData } from "../createEmptyData";
import { getTemplateFilename } from "../getTemplateFilename";
import { useNavigate } from "../ScreenProvider";
import { ScreenProps } from "./types";

export interface DownloadCreateTemplateScreenProps<Row> extends ScreenProps {
  onDownloadCSV: (args: { data: Row[]; filename: string }) => void;
  /**
   * @default false
   */
  selectDownloadTemplateColumns?: boolean;
  csvPrefix?: string;
  headers: string[];
  columns: CSVImportUIColumns;
  title?: string;
}

export const DownloadCreateTemplateScreen = <Row,>({
  headers,
  csvPrefix,
  onDownloadCSV,
  selectDownloadTemplateColumns = false,
  columns,
  title = "作成用CSVのダウンロード",
}: DownloadCreateTemplateScreenProps<Row>) => {
  const { onNavigate } = useNavigate();

  const tableData = Object.values(columns)
    .filter((column) => column.hiddenOnCreateTemplate !== true)
    .map((value) => {
      return {
        label: value.label,
        description: value.description,
        required: value.required,
      };
    });

  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  const handleDownloadCreateTemplate = () => {
    const filteredHeaders = selectDownloadTemplateColumns
      ? tableData
          .filter((_, index) => {
            if (tableData[index].required) {
              return true;
            }
            if (selectedIndexes.includes(index)) {
              return true;
            }
            return false;
          })
          .map((item) => item.label)
      : headers;
    const row = createEmptyData(filteredHeaders) as Row;
    onDownloadCSV({
      data: [row],
      filename: getTemplateFilename("import", csvPrefix),
    });
  };

  return (
    <ScreenLayout
      title={title}
      onNext={() => {
        onNavigate("loadCSV");
      }}
    >
      {selectDownloadTemplateColumns ? (
        <div
          css={css`
            display: grid;
            gap: 16px;
          `}
        >
          <div>
            <Typography>編集したい項目を選択してください。</Typography>
            <Typography variant="caption">
              ＊必須の項目は選択しない場合も、CSVファイルに出力されます。
            </Typography>
          </div>
          <TableUI
            onSelectAll={({ checked }) => {
              if (checked) {
                setSelectedIndexes(tableData.map((_, index) => index));
              } else {
                setSelectedIndexes([]);
              }
            }}
            onSelect={({ index, checked }) => {
              setSelectedIndexes((prev) => {
                if (checked) {
                  return [...prev, index];
                }
                return prev.filter((item) => item !== index);
              });
            }}
            selectedIndex={selectedIndexes}
            columns={[
              {
                key: "label",
                title: "カラム名",
              },
              {
                key: "description",
                title: "説明",
              },
              {
                key: "required",
                title: "必須",
                renderCell: ({ cell }) => {
                  return <span>{cell.value ? "YES" : "NO"}</span>;
                },
              },
            ]}
            data={tableData}
          />
        </div>
      ) : null}

      <div
        css={
          selectDownloadTemplateColumns
            ? css`
                display: grid;
                justify-content: flex-end;
              `
            : css`
                display: grid;
                justify-content: center;
              `
        }
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={handleDownloadCreateTemplate}
        >
          アップロード用CSVを取得
        </Button>
      </div>
    </ScreenLayout>
  );
};
